import { Injectable, inject } from "@angular/core";
import { environment } from "../../../environment";
import { SystemBannerService, type SystemBannerSeverity } from "./system-banner.service";

/**
 * System-wide announcement feed → the global system banner.
 *
 * Polls the backend's public GET /api/v1/system-messages/active (rows written
 * by deploys via --notify-users, or manually) every POLL_INTERVAL_MS and
 * immediately whenever the tab regains focus — the moment that matters, since
 * a user returning after hours should see "we deployed, refresh" right away.
 *
 * Message kinds:
 * - update: carries the deployed git sha. Hidden when it matches this
 *   bundle's environment.buildVersion, so a freshly refreshed client is
 *   never told to refresh again. Renders a Refresh action.
 * - info / warning: plain announcements.
 *
 * At most one refresh notice is ever on screen. A tab left open across two
 * deploys used to stack them (the poll only ever added banners, so the
 * superseded row stayed until the tab closed), so each poll now picks the
 * newest `action: "refresh"` message and clears any earlier refresh banner it
 * had shown. Non-refresh announcements are untouched and still stack.
 *
 * Dismissals persist in localStorage by message id — a new deploy inserts a
 * new row (new id), so each update prompts at most once per user. Hitting
 * Refresh counts as a dismissal too: the user did what was asked, so that
 * row must never nag them again regardless of which sha the reload lands
 * on. Relying on the sha match alone left the banner permanently stuck
 * whenever a deploy shipped without an announcement (a manual
 * full-build-deploy without --notify-users, 2026-09-05) or with a sha that
 * differed from the bundle's stamp.
 *
 * Design rationale and the accepted fix backlog for this subsystem live in
 * ai-context/decisions/system-wide-messaging_9-5-26.md — read it before
 * changing polling, dismissal, or sha self-suppression behavior.
 */

type SystemMessage = {
  id: string;
  kind: "update" | "info" | "warning";
  title: string | null;
  body: string;
  action: "refresh" | null;
  deployedSha: string | null;
  /** ISO timestamp from the server; used to pick the newest refresh notice. */
  createdAt?: string;
};

const POLL_INTERVAL_MS = 3 * 60_000;
const FETCH_TIMEOUT_MS = 10_000;
const DISMISSED_STORAGE_KEY = "tp-dismissed-system-messages";
const MAX_REMEMBERED_DISMISSALS = 50;

function bannerIdFor(message: SystemMessage): string {
  return `sysmsg-${message.id}`;
}

@Injectable({ providedIn: "root" })
export class SystemMessagesService {
  private banner = inject(SystemBannerService);
  private baseUrl = environment.backend.baseUrl.replace(/\/$/, "");
  private started = false;
  /** Banner ids of refresh notices shown so far, so a newer one can replace them. */
  private shownRefreshBannerIds = new Set<string>();

  /** Call once from the app shell; safe to call again (no-op). */
  start(): void {
    if (this.started || typeof window === "undefined") return;
    this.started = true;
    void this.poll();
    setInterval(() => void this.poll(), POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void this.poll();
    });
  }

  private async poll(): Promise<void> {
    let messages: SystemMessage[];
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/system-messages/active`, {
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
      if (!response.ok) return;
      messages = ((await response.json()) as { messages?: SystemMessage[] }).messages ?? [];
    } catch {
      return; // banners are a convenience; never surface fetch noise
    }

    const dismissed = this.dismissedIds();
    const valid = messages.filter((message) => message?.id && message.body);

    // Only one refresh notice at a time: the newest one wins and replaces any
    // earlier refresh banner still on screen. Older refresh rows are
    // superseded by definition, so they are never shown as a fallback.
    const latestRefresh = this.latestRefreshMessage(valid);
    const refreshToShow =
      latestRefresh &&
      !dismissed.has(bannerIdFor(latestRefresh)) &&
      !this.alreadyOnAnnouncedBuild(latestRefresh)
        ? latestRefresh
        : null;
    this.clearSupersededRefreshBanners(refreshToShow ? bannerIdFor(refreshToShow) : null);
    if (refreshToShow) this.showMessage(refreshToShow);

    for (const message of valid) {
      if (message.action === "refresh") continue;
      const bannerId = bannerIdFor(message);
      if (dismissed.has(bannerId)) continue;
      if (this.alreadyOnAnnouncedBuild(message)) continue;
      this.showMessage(message);
    }
  }

  /** Newest `action: "refresh"` message in the feed, or null when there is none. */
  private latestRefreshMessage(messages: SystemMessage[]): SystemMessage | null {
    // The route orders by created_at DESC, so the first match is already the
    // newest; createdAt is compared when present in case that ever changes.
    return messages
      .filter((message) => message.action === "refresh")
      .reduce<SystemMessage | null>((newest, message) => {
        if (!newest) return message;
        if (!message.createdAt || !newest.createdAt) return newest;
        return message.createdAt > newest.createdAt ? message : newest;
      }, null);
  }

  /**
   * Remove refresh banners this service showed that are no longer the current
   * one. `clear()` rather than `dismiss()` — the row was superseded, not acted
   * on, so nothing is written to the dismissal store. Only `sysmsg-` refresh
   * banners are touched; banners owned by other features are left alone.
   */
  private clearSupersededRefreshBanners(keepBannerId: string | null): void {
    for (const bannerId of this.shownRefreshBannerIds) {
      if (bannerId === keepBannerId) continue;
      this.banner.clear(bannerId);
      this.shownRefreshBannerIds.delete(bannerId);
    }
  }

  /** The client already runs the announced build — nothing to refresh. */
  private alreadyOnAnnouncedBuild(message: SystemMessage): boolean {
    return (
      message.kind === "update" &&
      !!message.deployedSha &&
      message.deployedSha === environment.buildVersion
    );
  }

  private showMessage(message: SystemMessage): void {
    const bannerId = bannerIdFor(message);
    const severity: SystemBannerSeverity = message.kind === "warning" ? "warning" : "info";
    if (message.action === "refresh") this.shownRefreshBannerIds.add(bannerId);
    this.banner.show({
      id: bannerId,
      severity,
      message: message.title ? `${message.title} — ${message.body}` : message.body,
      dismissible: true,
      onDismiss: () => this.rememberDismissal(bannerId),
      ...(message.action === "refresh"
        ? {
            action: {
              label: "Refresh",
              callback: () => {
                this.rememberDismissal(bannerId);
                this.reloadPage();
              },
            },
          }
        : {}),
    });
  }

  /** Seam for tests — jsdom's window.location cannot be replaced. */
  private reloadPage(): void {
    window.location.reload();
  }

  private dismissedIds(): Set<string> {
    try {
      const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : []);
    } catch {
      return new Set();
    }
  }

  private rememberDismissal(bannerId: string): void {
    try {
      const ids = [...this.dismissedIds(), bannerId].slice(-MAX_REMEMBERED_DISMISSALS);
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
    } catch {
      // localStorage unavailable (private mode etc.) — in-memory dismissal
      // in SystemBannerService still covers this page load.
    }
  }
}
